import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildMatchWire, buildRoundWire, getWire, listRounds, sendWire } from '../api/portal';
import { ApiError } from '../api/client';

function when(iso?: string) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('sr-RS', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

export function WirePage() {
  const qc = useQueryClient();
  const inbox = useQuery({ queryKey: ['portal-wire'], queryFn: getWire, refetchInterval: 12_000 });
  const rounds = useQuery({ queryKey: ['portal-rounds'], queryFn: listRounds });
  const [matchId, setMatchId] = useState('');
  const [roundKey, setRoundKey] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const data = inbox.data;

  const refresh = () => qc.invalidateQueries({ queryKey: ['portal-wire'] });
  const send = useMutation({
    mutationFn: sendWire,
    onSuccess: (row) => { setMsg(row.status === 'sent' ? 'Drop poslat.' : (row.error || row.status)); refresh(); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });
  const buildM = useMutation({
    mutationFn: () => buildMatchWire(matchId.trim()),
    onSuccess: (row) => { setMsg(`Match drop ${row.status}`); refresh(); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });
  const buildR = useMutation({
    mutationFn: () => buildRoundWire(roundKey),
    onSuccess: (row) => { setMsg(`Round drop ${row.status}`); refresh(); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <div>
      <div className="page-head">
        <div className="page-kicker">Delivery</div>
        <h1 className="page-title">Origo Wire</h1>
        <p className="page-sub">
          Jedan drop posle gola i posle meča. Telegram / folder / javna traka.
          Plan: <span className="text-white">{data?.plan || '…'}</span>
          {data?.wire_url ? <> · <a className="text-white underline" href={data.wire_url} target="_blank" rel="noreferrer">{data.wire_url}</a></> : null}
          {data?.obs_url ? <> · <a className="text-white underline" href={data.obs_url} target="_blank" rel="noreferrer">OBS</a></> : null}
        </p>
      </div>

      {inbox.isError && (
        <div className="alert-bad mb-3">{inbox.error instanceof ApiError ? inbox.error.message : String(inbox.error)}</div>
      )}
      {msg && <div className="card mb-3 p-3 text-sm text-slate-200">{msg}</div>}

      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <div className="card p-4">
          <div className="text-[11px] uppercase text-slate-500">SLA</div>
          <div className="mt-1 text-sm text-white">
            Kasni: {data?.sla.overdue ?? 0} · Medij kasni: {data?.sla.late_media ?? 0}
          </div>
          <p className="mt-2 text-[12px] text-slate-500">
            Flash 30s · Match 5 min posle official · Round 10 min posle poslednjeg FT.
            Bez klipa drop i dalje ide.
          </p>
        </div>
        <div className="card p-4">
          <div className="text-[11px] uppercase text-slate-500">Pošalji sad</div>
          <div className="mt-2 flex flex-wrap gap-2">
            <input className="input max-w-xs" value={matchId} onChange={(e) => setMatchId(e.target.value)} placeholder="match UUID" />
            <button type="button" className="btn-primary text-xs" disabled={!matchId.trim() || buildM.isPending} onClick={() => buildM.mutate()}>
              Match drop
            </button>
          </div>
          {(rounds.data ?? []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              <select className="input max-w-xs" value={roundKey} onChange={(e) => setRoundKey(e.target.value)}>
                <option value="">Kolo</option>
                    {(rounds.data ?? []).map((r) => (
                  <option key={r.key} value={r.key}>{r.label || r.key}</option>
                ))}
              </select>
              <button type="button" className="btn-secondary text-xs" disabled={!roundKey || buildR.isPending} onClick={() => buildR.mutate()}>
                Round drop
              </button>
            </div>
          )}
          <p className="mt-2 text-[12px] text-slate-500">
            Destinacija se uzima sa <Link className="text-white underline" to="/webhooks">Webhooks</Link> (Telegram prvo, zatim folder).
          </p>
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th>Drop</th>
              <th>Status</th>
              <th>Dest</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(data?.drops ?? []).map((d) => {
              const copy = d.card?.copy?.telegram || d.card?.copy?.press_lead || d.kind;
              return (
                <tr key={d.id}>
                  <td>
                    <div className="text-white">{copy}</div>
                    <div className="text-[11px] text-slate-500">
                      {d.kind} · {when(d.created_at)}
                      {d.media_late ? ' · medij kasni' : ''}
                      {d.overdue ? ' · SLA' : ''}
                    </div>
                  </td>
                  <td className="tabular-nums text-slate-300">{d.status}</td>
                  <td className="max-w-[14rem] truncate text-[12px] text-slate-400">{d.dest || d.error || '—'}</td>
                  <td className="text-right">
                    <button type="button" className="btn-ghost text-xs" disabled={send.isPending} onClick={() => send.mutate(d.id)}>
                      Pošalji
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {(data?.drops ?? []).length === 0 && (
          <div className="p-4 text-sm text-slate-500">Nema dropova. Official pečat ili gol sa Save diže prvi.</div>
        )}
      </div>
    </div>
  );
}
